import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import mapValues from 'lodash/mapValues'
import omitBy from 'lodash/omitBy'
import { ArrayParam, BooleanParam, NumberParam, NumericObjectParam, ObjectParam } from 'use-query-params'

import { CateCity } from '@/context/Cities'
import {
  Jsonb_Comparison_Exp,
  Listings_Detail_Bool_Exp,
  Listings_Detail_Order_By,
} from '@/types/cate-graphql-generated-types'
import { Page, QueryParams } from '@/types/filter'
import { Filter, FilterByPrice } from '@/types/graphql-generated-types'
import { SortBy, SortDirection, SortOptions } from '@/types/sort'
import {
  mapArrayFilter,
  mapBooleanFilter,
  mapBooleanFilterToIntComparison,
  mapComparisonExp,
  mapCounter,
  mapIntRangeFilter,
  mapSortDirection,
} from '@/utils/graphql-filter-mapper'

export const mapSort = (by: SortBy, direction: SortDirection): Listings_Detail_Order_By => {
  if (by === null) {
    return {
      ranking: mapSortDirection(direction),
    }
  }

  switch (by) {
    case SortBy.Area:
      return {
        detail: {
          commercialArea: mapSortDirection(direction),
        },
      }
    case SortBy.Date:
      return {
        publishedAt: mapSortDirection(direction),
      }
    case SortBy.Price:
      return {
        detail: {
          sellingPrices: {
            cents: mapSortDirection(direction),
          },
        },
      }
    case SortBy.Ranking:
      return {
        ranking: mapSortDirection(direction),
      }
  }
}

export const mapPaginate = (paginate?: { page?: number; size?: number }): { limit: number | null; offset: number } => {
  const sizeWithDefault = paginate?.size ?? 0
  const pageWithDefault = paginate?.page ?? 0

  return {
    limit: paginate?.size ?? null,
    offset: Math.max(sizeWithDefault * (pageWithDefault - 1), 0),
  }
}

type FloorPositionCate = {
  floorPosition: Jsonb_Comparison_Exp
}

export const mapFilter = (filter: Filter, citiesCate: CateCity[]): Listings_Detail_Bool_Exp => {
  const geoSlugFilter = mapValues(
    groupBy(
      filter.byGeoSlug?.map((zone) => zone.split('/')),
      'length'
    ),
    (value) => value.map((item) => item.join('/'))
  )

  return omitBy(
    {
      _and: {
        ...omitBy(
          {
            _and: {
              _or: [
                filter.byFloor?.map((floor): { detail: FloorPositionCate } => ({
                  detail: {
                    floorPosition: {
                      _contains: floor.toLowerCase(),
                    },
                  },
                })),
              ]
                .flat(1)
                .map((item) => omitBy(item, isNil || isEmpty))
                .filter((item) => Boolean(item) && !isEmpty(item)),
            },
          },
          (item) => isEmpty(item._or)
        ),
        ...omitBy(
          {
            _or: [
              omitBy(
                {
                  detail: omitBy(
                    {
                      city: mapArrayFilter(
                        geoSlugFilter['2']?.map((slug) =>
                          citiesCate.find((city) => city.slug === slug)?.slug?.toLowerCase()
                        )
                      ),
                    },
                    isNil || isEmpty
                  ),
                },
                isEmpty
              ),
              omitBy({ detail: { zone: mapComparisonExp('slug', mapArrayFilter(geoSlugFilter['3'])) } }, (item) =>
                isNil(item.zone)
              ),
            ]
              .flat(1)
              .map((item) => omitBy(item, isNil || isEmpty))
              .filter((item) => Boolean(item) && !isEmpty(item)),
          },
          isEmpty
        ),
      },
      additionalInfo: omitBy(
        {
          constructionYear: omitBy(mapIntRangeFilter(filter.byConstructionYear), isNil),
          coolingSystem: omitBy(
            mapArrayFilter(
              filter.onlyWithCoolingSystem ? ['autonomous', 'ready_for_installation', 'shared'] : [] // TODO: we could use _nin instead of _in to semplify the where's value
            ),
            isNil
          ),
          heatingSystem: omitBy(mapArrayFilter(filter.byHeatingSystem), isNil),
          stateAtDeed: omitBy(mapArrayFilter(filter.byStateAtDeed), isNil),
        },
        isEmpty
      ),
      characteristics: omitBy(
        {
          balconies: mapCounter(mapBooleanFilterToIntComparison(filter.onlyWithBalcony)),
          bathrooms: mapIntRangeFilter(filter.byBathrooms),
          bedrooms: mapIntRangeFilter(filter.byBedrooms),
          rooms: mapIntRangeFilter(filter.byRooms),
          stateOfMaintenance: mapArrayFilter(filter.byStateOfMaintenance),
          terraces: mapCounter(mapBooleanFilterToIntComparison(filter.onlyWithTerrace)),
        },
        isEmpty
      ),
      contactInfo: omitBy(
        {
          ownedByCasavo: mapBooleanFilter(filter.onlyCasavo),
        },
        isEmpty
      ),
      detail: omitBy(
        {
          commercialArea: mapIntRangeFilter(filter.byArea),
          sellingPrices: mapComparisonExp(
            'cents',
            mapIntRangeFilter(mapValues(filter.byPrice, (x) => (isNil(x) ? null : x * 100)))
          ),
        },
        isEmpty
      ),
      residentialType: mapArrayFilter(filter.byResidentialType),
      services: omitBy(
        {
          basement: mapBooleanFilter(filter.onlyWithBasement),
          box: mapCounter(mapBooleanFilterToIntComparison(filter.onlyWithBox)),
          conciergeService: mapBooleanFilter(filter.onlyWithConciergeService),
          elevator: mapBooleanFilter(filter.onlyWithElevator),
          garden: mapBooleanFilter(filter.onlyWithGarden),
          parkingSpaces: mapBooleanFilter(filter.onlyWithParking),
          pool: mapBooleanFilter(filter.onlyWithPool),
        },
        isEmpty
      ),
    },
    isEmpty
  )
}

export const priorityFilter: (keyof Filter)[] = ['byPrice', 'byRooms', 'byArea']
export const skipFilters: (keyof Filter)[] = ['onlyCasavo', 'byZone']

export const queryParams = {
  byArea: NumericObjectParam,
  byBathrooms: NumericObjectParam,
  byBedrooms: NumericObjectParam,
  byConstructionYear: NumericObjectParam,
  byFloor: ArrayParam,
  byHeatingSystem: ArrayParam,
  byPrice: NumericObjectParam,
  byResidentialType: ArrayParam,
  byRooms: NumericObjectParam,
  byStateAtDeed: ArrayParam,
  byStateOfMaintenance: ArrayParam,
  byZone: ArrayParam,
  onlyCasavo: BooleanParam,
  onlyWithBalcony: BooleanParam,
  onlyWithBasement: BooleanParam,
  onlyWithBox: BooleanParam,
  onlyWithConciergeService: BooleanParam,
  onlyWithCoolingSystem: BooleanParam,
  onlyWithElevator: BooleanParam,
  onlyWithGarden: BooleanParam,
  onlyWithParking: BooleanParam,
  onlyWithPool: BooleanParam,
  onlyWithTerrace: BooleanParam,
  pag: NumberParam,
  sorting: ObjectParam,
}

export const PAGINATION_SIZE = 20
export const MARKER_PAGINATION_SIZE = 500
export const MARKER_QUERY_SIZE = 50

export const cleanQueryParams = (queryParams: unknown): QueryParams =>
  Object.keys(queryParams).reduce((acc, key) => {
    if (queryParams[key] === undefined || queryParams[key] === null) {
      return acc
    }
    if (typeof queryParams[key] === 'object' && Object.keys(queryParams[key]).length === 0) {
      return acc
    }
    return { ...acc, [key]: queryParams[key] }
  }, {} as QueryParams)

export const cleanPriorityFilter = (cleanedQueryParams: QueryParams): Filter =>
  Object.keys(cleanedQueryParams).reduce((acc, key: keyof Filter) => {
    if (priorityFilter.includes(key) || skipFilters.includes(key)) {
      const queryParamsFilter = cleanedQueryParams[key]
      if (queryParamsFilter && typeof queryParamsFilter !== 'boolean' && key !== 'byZone') {
        const filter: FilterByPrice = {
          from: queryParamsFilter.from ? queryParamsFilter.from : null,
          to: queryParamsFilter.to ? queryParamsFilter.to : null,
        }
        return { ...acc, [key]: filter }
      }
      return { ...acc, [key]: queryParamsFilter }
    }
    return acc
  }, {} as Filter)

export const cleanOtherFilters = (cleanedQueryParams: QueryParams): Filter =>
  Object.keys(cleanedQueryParams).reduce((acc, k: keyof QueryParams) => {
    const key = k.trim() as keyof QueryParams
    if (key !== 'sorting' && key !== 'pag' && !priorityFilter.includes(key) && !skipFilters.includes(key)) {
      let filter = cleanedQueryParams[key]
      if (key === 'byConstructionYear') {
        filter = cleanedQueryParams[key]
        if (filter) {
          if (!filter.to) {
            filter.to = null
          }
          if (!filter.from) {
            filter.from = null
          }
        }
      }
      return { ...acc, [key]: filter }
    }
    return acc
  }, {} as Filter)

export const cleanSortingParams = (
  cleanedQueryParams: QueryParams,
  defaultSortBy: SortBy = SortBy.Ranking
): SortOptions => ({
  by: cleanedQueryParams['sorting']?.by || defaultSortBy,
  direction: cleanedQueryParams['sorting']?.direction || SortDirection.Desc,
})

export const cleanPageParams = (queryParams: QueryParams): Page =>
  Object.keys(queryParams).reduce((acc, key) => {
    const pageKey = 'pag'
    const page = queryParams[pageKey]

    return key.trim() === pageKey && page >= 1 ? page : acc
  }, 1)
